import { toLabelValue } from '@/common/convertToDropdownOptions';
import { decrypt } from '@/common/securityUtils';
import { Can } from '@/components/can/Can';
import { Form, useForm } from '@/components/forms';
import { Spinner } from '@/components/Spinner';
import { Separator } from '@/design-system/separator';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useAttributes } from '@/hooks/useAttributes';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { commaSeparator } from '@/lib/commaSeparator';
import { uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from 'ui/button';
import {
  affiliateFormDefaultValues,
  affiliateToFormValues,
  AffiliateFormValues,
  formValuesToAffiliatePayload,
} from './affiliate-form.mapper';
import useAffiliateDetail from './hooks/useAffiliateDetail';
import useUpdateAffiliateDetail from './hooks/useUpdateAffiliateDetail';
import { AffiliateFormSchema } from './schema';
import { AddressSection } from './sections/AddressSection';
import { BusinessSection } from './sections/BusinessSection';
import { ProfileSection } from './sections/ProfileSection';

const CORPORATION_TYPE = '1';

const getImageUrl = async (image: AffiliateFormValues['image']) => {
  if (typeof image === 'string') return image;
  if (image && typeof image === 'object' && 'file' in image) {
    return uploadFileToS3WithPresignedUrl(image);
  }
  if (image && typeof image === 'object' && 'path' in image) return image.path;
  return '';
};

export const AffiliateForm = () => {
  const { affiliateId } = useParams({ from: '/(app)/users/affiliates/$affiliateId' });
  const { affiliateDetail, isSuccess, isPending, dataUpdatedAt } = useAffiliateDetail(affiliateId);
  const { mutateAsync, isPending: isSaving } = useUpdateAffiliateDetail();
  const { data = [] } = useAttributes('businessType');
  const { t } = useDynamicLocale();
  const businessTypeOptions = toLabelValue(data, { labelKey: 'label', valueKey: 'value' });

  const form = useForm({
    schema: AffiliateFormSchema,
    mode: 'onBlur',
    defaultValues: affiliateFormDefaultValues,
  });
  const businessType = useWatch({ control: form.control, name: 'businessType' });
  const isCorporation = businessType !== 'default' && decrypt(businessType) === CORPORATION_TYPE;

  useEffect(() => {
    if (isSuccess && affiliateDetail) form.reset(affiliateToFormValues(affiliateDetail));
  }, [affiliateDetail, dataUpdatedAt, form, isSuccess]);

  const submit = async (values: AffiliateFormValues) => {
    const imageUrl = await getImageUrl(values.image);
    if (!imageUrl) {
      toast.error(t('common.form.validation.profileUpload'));
      return;
    }

    await mutateAsync(formValuesToAffiliatePayload(affiliateId, values, imageUrl, isCorporation));
  };

  if (isPending) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Form form={form} onSubmit={submit}>
      <div className="flex flex-col gap-6">
        <div className="text-sm bg-info inline-flex p-3 text-on-color rounded w-fit">
          <Translation id="affiliate.page.affiliateInfoForm.reward" />: {commaSeparator(affiliateDetail.rewardPoints)}{' '}
          円
        </div>
        <ProfileSection defaultImageUrl={affiliateDetail.image?.path} />
        <AddressSection />
        <BusinessSection
          options={businessTypeOptions}
          isCorporation={isCorporation}
          selectedBusinessType={affiliateDetail.businessType}
        />
      </div>

      <Separator className="mt-8 mb-6" variant="muted" thickness="lg" />
      <div className="flex w-full justify-between">
        <Link to="/users/affiliates">
          <Button variant="outline" className="w-[140px] h-10 bg-card border-input" type="button">
            <Translation id="affiliate.page.affiliateInfoForm.cancel" />
          </Button>
        </Link>
        <Can rule={{ anyOf: [PERMS.affiliate.manage] }}>
          <Button type="submit" className="w-[110px] h-10" isLoading={isSaving}>
            <Translation id="affiliate.page.affiliateInfoForm.save" />
          </Button>
        </Can>
      </div>
    </Form>
  );
};
